"use client";

import type { CSSProperties } from "react";

import { cn } from "@repo/blacksmith-ui/cn";
import { assetUrl } from "@/lib/assetUrl";

/**
 * Animated hammer icon for in-progress autofix indicators.
 *
 * The PNGs are APNG (Animated PNG) files – the browser plays the animation
 * natively, so no JS frame cycling is needed. Inline width/height/maxWidth
 * override Tailwind preflight (`img { max-width:100%; height:auto }`).
 *
 * Defaults to 16px so the autofix status slot matches every other pill
 * status icon; callers in tighter rows pass a smaller `size`.
 *
 * `inCircleBadge` centers the hammer's swing inside the docked badge's
 * circle. On the 28×28 canvas the swing spans x 2-25 at any alpha, x 3-24
 * opaque, y 0-27; the resting strike pose alone sits ~2px right and ~5.5px
 * low, and centering it would carry the raised hammer outside the circle.
 * The swing's 50%-coverage box sits 0.19px left and 0.22px high at 16px.
 * Scaling to 15px keeps its soft edge inside the arc (8.82px at 5% vs the
 * arc's 8.9px); scale, not `size`, because a 15px box centers on a half
 * pixel that Chromium snaps 0.5px right and low.
 */
export function AutofixHammerIcon({
  className,
  size = 16,
  inCircleBadge = false,
}: {
  className?: string;
  size?: number;
  inCircleBadge?: boolean;
}) {
  const imgStyle: CSSProperties = {
    width: size,
    height: size,
    maxWidth: "none",
  };

  return (
    <span
      className={cn(
        "relative shrink-0",
        inCircleBadge
          ? "block translate-x-[1.1%] translate-y-[1.31%] scale-[0.9375]"
          : "inline-block",
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={assetUrl("autofix/hammer-light.png")}
        alt=""
        draggable={false}
        className="absolute inset-0 select-none dark:hidden"
        style={imgStyle}
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={assetUrl("autofix/hammer-dark.png")}
        alt=""
        draggable={false}
        className="absolute inset-0 hidden select-none dark:block"
        style={imgStyle}
      />
    </span>
  );
}
